import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

export enum OrderStatus {
  Created = "CREATED",
  Pending = "PENDING",
  Preparing = "PREPARING",
  Completed = "COMPLETED",
  Cancelled = "CANCELLED"
}

export interface OrderLine {
  id: string;
  orderId: string;
  productId: string;
  quantity: number;
}

export interface Order {
  id: string;
  createdAt: string;
  updatedAt: string;
  orderNumber: string;
  eventId: string;
  userId: string;
  status: OrderStatus;
  totalPrice: number;
  orderLines: OrderLine[];
  customerNote: string;
  delivery: boolean;
  deliveryLocation: string;
  completedBeverages?: boolean;
}

export const useOrderStore = defineStore('orders', () => {
  const orders = ref<Order[]>([])

  // Computed properties for filtered orders
  const createdOrders = computed(() => {
    return orders.value
      .filter(order => order.status === OrderStatus.Pending)
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
  })

  const pendingOrders = computed(() => {
    return orders.value
      .filter(order => order.status === OrderStatus.Preparing)
      .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
  })

  const completedOrders = computed(() => {
    return orders.value
      .filter(order => order.status === OrderStatus.Completed)
      .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
  })

  // Actions
  function setOrders(newOrders: Order[]) {
    // For each new order, check if it exists in the current store
    // If it exists and its status is not Pending, preserve its current status
    const updatedOrders = newOrders.map(newOrder => {
      const existingOrder = orders.value.find(o => o.id === newOrder.id);
      if (existingOrder && existingOrder.status !== OrderStatus.Pending) {
        // Preserve the existing order's status and other status-related fields
        return {
          ...newOrder,
          status: existingOrder.status,
          updatedAt: existingOrder.updatedAt,
          completedBeverages: existingOrder.completedBeverages
        };
      }
      return newOrder;
    });

    orders.value = updatedOrders;
  }

  function updateOrderStatus(orderId: string, newStatus: OrderStatus) {
    const order = orders.value.find(o => o.id === orderId);
    if (order) {
      order.status = newStatus;
      order.updatedAt = new Date().toISOString();
    }
  }

  function toggleBeveragesCompleted(orderId: string) {
    const order = orders.value.find(o => o.id === orderId)
    if (order) {
      order.completedBeverages = !order.completedBeverages
      order.updatedAt = new Date().toISOString()
    }
  }

  return {
    orders,
    createdOrders,
    pendingOrders,
    completedOrders,
    setOrders,
    updateOrderStatus,
    toggleBeveragesCompleted
  }
}, {
  persist: true // Enable state persistence
})
